import { HOV_CELL_COUNT } from "@/features/hoverIdle/core/hoverIdleState";
import {
  clampMpmAssignmentsToCapacity,
  normalizeMpmState,
  MPM_MHOV_EXECUTION_MP_COST,
  MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH,
  MPM_OHOV_EXECUTION_MP_COST,
  type MpmState,
} from "@/features/hoverIdle/mpm/mpmState";
import {
  computeMpmAssignedMpOccupancy,
  computeMpmAvailableMpFromOccupancy,
  isMpmAmplifierPanelsActive,
  isMpmAmplifierUpgradesActive,
  isMpmHovGainUpgradesActive,
  isMpmHovTargetsActive,
  isMpmMHovExecutionActive,
  isMpmNHovExecutionActive,
  isMpmOHovExecutionActive,
} from "@/features/hoverIdle/mpm/mpmCapacity";
import {
  resolveMpmAmplifierPanelAssignAllState,
  resolveMpmExplorationAssignAllState,
  resolveMpmHovGainUpgradesAssignAllState,
  resolveMpmHovTargetsAssignAllState,
  type MpmAssignAllAmplifierEntryInput,
} from "@/features/hoverIdle/mpm/mpmAssignAll";
import {
  toHovGainUpgradeMpTargetKey,
  type HovGainUpgradeMpTarget,
} from "@/features/hoverIdle/mpm/mpmHovGainUpgradeTargets";
import type { HovAmplifierEntryId } from "@/features/hoverIdle/hov/hovAmplifierModel";
import { isNHovBranchId } from "@/features/hoverIdle/nHov/nHovBranches";
import type { NHovBranchId } from "@/features/hoverIdle/types";
import type { ExplorationMpSlotDefinition } from "@/features/hoverIdle/exploration/explorationTypes";

// Pure command resolvers for MPM assignment state transitions.
//
// Each resolver receives the current MpmState plus the availability input
// the caller already knows, and returns either { ok: true, state: <next> }
// or { ok: false, state: <unchanged> }. Callers (currently HoverIdleModel)
// own the authoritative MpmState and decide when to apply the result.
//
// Boundary:
//   - direct MP assignment to Hov targets / Hov Gain Upgrades / Amp Panel
//   - MPM-managed MHov execution assignment (state transition only;
//     "when to auto-run" stays out of this module)
//   - section-level Clear All / Assign All commands
//
// Display / latch helpers (section header visibility, the shared Assign All
// latch derivation) live in `mpmAssignAll.ts` and are reused here for the
// Assign All commands' execution gating.

export interface MpmAssignmentCommandResult {
  readonly ok: boolean;
  readonly state: MpmState;
}

function failure(state: MpmState): MpmAssignmentCommandResult {
  return { ok: false, state };
}

function success(state: MpmState): MpmAssignmentCommandResult {
  return { ok: true, state };
}

function isValidHovCellIndex(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value < HOV_CELL_COUNT;
}

function hasAvailableMp(state: MpmState, mpCapacity: number): boolean {
  return mpCapacity > 0 && computeMpmAssignedMpOccupancy(state) < mpCapacity;
}

function applyAssignment(state: MpmState, mpCapacity: number): MpmState {
  return clampMpmAssignmentsToCapacity(normalizeMpmState(state), mpCapacity);
}

function applyRemoval(state: MpmState): MpmState {
  return normalizeMpmState(state);
}

// --- Hov targets (direct MP) -------------------------------------------------

export interface AssignMpToHovTargetInput {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly cellIndex: number;
  // When provided, the resolver gates assignment on Hov-screen visibility /
  // unlock. The MPM panel may assign by permanent target index even when the
  // Hov screen currently hides or locks the cell (e.g., after MHov resets the
  // grid back to tier 1), in which case both fields are omitted.
  readonly requireHovScreenCellVisible?: boolean;
  readonly requireHovScreenCellUnlocked?: boolean;
}

export function resolveAssignMpToHovTarget(
  input: AssignMpToHovTargetInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity, cellIndex } = input;
  if (!isMpmHovTargetsActive(state)) return failure(state);
  if (!hasAvailableMp(state, mpCapacity)) return failure(state);
  if (!isValidHovCellIndex(cellIndex)) return failure(state);
  if (input.requireHovScreenCellVisible === false) return failure(state);
  if (input.requireHovScreenCellUnlocked === false) return failure(state);
  if (state.hovTargets.assignments.includes(cellIndex)) return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        hovTargets: {
          ...state.hovTargets,
          assignments: [...state.hovTargets.assignments, cellIndex],
        },
      },
      mpCapacity,
    ),
  );
}

export function resolveRemoveMpFromHovTarget(input: {
  readonly state: MpmState;
  readonly cellIndex: number;
}): MpmAssignmentCommandResult {
  const { state, cellIndex } = input;
  if (!state.hovTargets.assignments.includes(cellIndex)) return failure(state);
  return success(
    applyRemoval({
      ...state,
      hovTargets: {
        ...state.hovTargets,
        assignments: state.hovTargets.assignments.filter(
          (assigned) => assigned !== cellIndex,
        ),
      },
    }),
  );
}

export function resolveClearMpmHovTargetAssignments(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (state.hovTargets.assignments.length === 0) return failure(state);
  return success(
    applyRemoval({
      ...state,
      hovTargets: { ...state.hovTargets, assignments: [] },
    }),
  );
}

// --- Hov Gain Upgrades (direct MP) ------------------------------------------

export interface AssignMpToHovGainUpgradeInput {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly target: HovGainUpgradeMpTarget;
}

export function resolveAssignMpToHovGainUpgrade(
  input: AssignMpToHovGainUpgradeInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity, target } = input;
  if (!isMpmHovGainUpgradesActive(state)) return failure(state);
  if (!hasAvailableMp(state, mpCapacity)) return failure(state);
  const targetKey = toHovGainUpgradeMpTargetKey(target);
  if (targetKey === null) return failure(state);
  const alreadyAssigned = state.hovGainUpgrades.assignments.some(
    (assigned) => toHovGainUpgradeMpTargetKey(assigned) === targetKey,
  );
  if (alreadyAssigned) return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        hovGainUpgrades: {
          ...state.hovGainUpgrades,
          assignments: [...state.hovGainUpgrades.assignments, target],
        },
      },
      mpCapacity,
    ),
  );
}

export function resolveRemoveMpFromHovGainUpgrade(input: {
  readonly state: MpmState;
  readonly target: HovGainUpgradeMpTarget;
}): MpmAssignmentCommandResult {
  const { state, target } = input;
  const targetKey = toHovGainUpgradeMpTargetKey(target);
  if (targetKey === null) return failure(state);
  const present = state.hovGainUpgrades.assignments.some(
    (assigned) => toHovGainUpgradeMpTargetKey(assigned) === targetKey,
  );
  if (!present) return failure(state);
  return success(
    applyRemoval({
      ...state,
      hovGainUpgrades: {
        ...state.hovGainUpgrades,
        assignments: state.hovGainUpgrades.assignments.filter(
          (assigned) => toHovGainUpgradeMpTargetKey(assigned) !== targetKey,
        ),
      },
    }),
  );
}

export function resolveClearMpmHovGainUpgradeAssignments(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (state.hovGainUpgrades.assignments.length === 0) return failure(state);
  return success(
    applyRemoval({
      ...state,
      hovGainUpgrades: { ...state.hovGainUpgrades, assignments: [] },
    }),
  );
}

// --- Amp Panel (direct MP) --------------------------------------------------

export interface AssignMpToAmplifierPanelInput {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly entryId: HovAmplifierEntryId;
  readonly entryUnlocked: boolean;
}

export function resolveAssignMpToAmplifierPanel(
  input: AssignMpToAmplifierPanelInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity, entryId, entryUnlocked } = input;
  if (!isMpmAmplifierPanelsActive(state)) return failure(state);
  if (!hasAvailableMp(state, mpCapacity)) return failure(state);
  if (!entryUnlocked) return failure(state);
  if (state.amplifierPanels.assignments.includes(entryId))
    return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        amplifierPanels: {
          ...state.amplifierPanels,
          assignments: [...state.amplifierPanels.assignments, entryId],
        },
      },
      mpCapacity,
    ),
  );
}

export function resolveRemoveMpFromAmplifierPanel(input: {
  readonly state: MpmState;
  readonly entryId: HovAmplifierEntryId;
}): MpmAssignmentCommandResult {
  const { state, entryId } = input;
  if (!state.amplifierPanels.assignments.includes(entryId)) {
    return failure(state);
  }
  return success(
    applyRemoval({
      ...state,
      amplifierPanels: {
        ...state.amplifierPanels,
        assignments: state.amplifierPanels.assignments.filter(
          (assigned) => assigned !== entryId,
        ),
      },
    }),
  );
}

export function resolveClearMpmAmplifierPanelAssignments(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (state.amplifierPanels.assignments.length === 0) return failure(state);
  return success(
    applyRemoval({
      ...state,
      amplifierPanels: { ...state.amplifierPanels, assignments: [] },
    }),
  );
}

// --- Amplifier upgrade actions (direct MP) ----------------------------------

export interface AssignMpToAmplifierUpgradeInput {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly entryId: HovAmplifierEntryId;
  readonly entryUnlocked: boolean;
}

export function resolveAssignMpToAmplifierUpgrade(
  input: AssignMpToAmplifierUpgradeInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity, entryId, entryUnlocked } = input;
  if (!isMpmAmplifierUpgradesActive(state)) return failure(state);
  if (!hasAvailableMp(state, mpCapacity)) return failure(state);
  if (!entryUnlocked) return failure(state);
  if (state.amplifierUpgrades.assignments.includes(entryId)) {
    return failure(state);
  }
  return success(
    applyAssignment(
      {
        ...state,
        amplifierUpgrades: {
          ...state.amplifierUpgrades,
          assignments: [...state.amplifierUpgrades.assignments, entryId],
        },
      },
      mpCapacity,
    ),
  );
}

export function resolveRemoveMpFromAmplifierUpgrade(input: {
  readonly state: MpmState;
  readonly entryId: HovAmplifierEntryId;
}): MpmAssignmentCommandResult {
  const { state, entryId } = input;
  if (!state.amplifierUpgrades.assignments.includes(entryId)) {
    return failure(state);
  }
  return success(
    applyRemoval({
      ...state,
      amplifierUpgrades: {
        ...state.amplifierUpgrades,
        assignments: state.amplifierUpgrades.assignments.filter(
          (assigned) => assigned !== entryId,
        ),
      },
    }),
  );
}

export function resolveClearMpmAmplifierUpgradeAssignments(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (state.amplifierUpgrades.assignments.length === 0) return failure(state);
  return success(
    applyRemoval({
      ...state,
      amplifierUpgrades: { ...state.amplifierUpgrades, assignments: [] },
    }),
  );
}

// --- MHov execution (MPM-managed assignment state transition only) ----------

export function resolveAssignMpToMHovExecution(input: {
  readonly state: MpmState;
  readonly mpCapacity: number;
}): MpmAssignmentCommandResult {
  const { state, mpCapacity } = input;
  if (!isMpmMHovExecutionActive(state)) return failure(state);
  if (state.mhovExecution.assigned) return failure(state);
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity,
    state,
  });
  if (availableMp < MPM_MHOV_EXECUTION_MP_COST) return failure(state);
  return success(
    applyRemoval({
      ...state,
      mhovExecution: {
        ...state.mhovExecution,
        assigned: true,
        runtime: { pending: false },
      },
    }),
  );
}

export function resolveRemoveMpFromMHovExecution(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (!state.mhovExecution.assigned) return failure(state);
  return success(
    applyRemoval({
      ...state,
      mhovExecution: {
        ...state.mhovExecution,
        assigned: false,
        runtime: { pending: false },
      },
    }),
  );
}

// --- NHov execution (MPM-managed assignment state transition only) ----------

export function resolveAssignMpToNHovExecution(input: {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly branchId: NHovBranchId;
}): MpmAssignmentCommandResult {
  const { state, mpCapacity, branchId } = input;
  if (!isMpmNHovExecutionActive(state)) return failure(state);
  if (!isNHovBranchId(branchId)) return failure(state);
  if (state.nhovExecution.assignments.includes(branchId)) {
    return failure(state);
  }
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity,
    state,
  });
  if (availableMp < MPM_NHOV_EXECUTION_MP_COST_PER_BRANCH) {
    return failure(state);
  }
  return success(
    applyRemoval({
      ...state,
      nhovExecution: {
        ...state.nhovExecution,
        assignments: [...state.nhovExecution.assignments, branchId],
      },
    }),
  );
}

export function resolveRemoveMpFromNHovExecution(input: {
  readonly state: MpmState;
  readonly branchId: NHovBranchId;
}): MpmAssignmentCommandResult {
  const { state, branchId } = input;
  if (!isNHovBranchId(branchId)) return failure(state);
  if (!state.nhovExecution.assignments.includes(branchId)) {
    return failure(state);
  }
  return success(
    applyRemoval({
      ...state,
      nhovExecution: {
        ...state.nhovExecution,
        assignments: state.nhovExecution.assignments.filter(
          (assigned) => assigned !== branchId,
        ),
      },
    }),
  );
}

// --- OHov execution (MPM-managed assignment state transition only) ----------

export function resolveAssignMpToOHovExecution(input: {
  readonly state: MpmState;
  readonly mpCapacity: number;
}): MpmAssignmentCommandResult {
  const { state, mpCapacity } = input;
  if (!isMpmOHovExecutionActive(state)) return failure(state);
  if (state.ohovExecution.assigned) return failure(state);
  const availableMp = computeMpmAvailableMpFromOccupancy({
    mpCapacity,
    state,
  });
  if (availableMp < MPM_OHOV_EXECUTION_MP_COST) return failure(state);
  return success(
    applyRemoval({
      ...state,
      ohovExecution: {
        ...state.ohovExecution,
        assigned: true,
      },
    }),
  );
}

export function resolveRemoveMpFromOHovExecution(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (!state.ohovExecution.assigned) return failure(state);
  return success(
    applyRemoval({
      ...state,
      ohovExecution: {
        ...state.ohovExecution,
        assigned: false,
      },
    }),
  );
}

// --- Assign All commands ----------------------------------------------------

export type AssignAllHovTargetsInput = {
  readonly state: MpmState;
  readonly mpCapacity: number;
};

export function resolveAssignAllMpmHovTargets(
  input: AssignAllHovTargetsInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity } = input;
  if (!isMpmHovTargetsActive(state)) return failure(state);
  const evaluation = resolveMpmHovTargetsAssignAllState({
    mpCapacity,
    mpm: state,
  });
  if (!evaluation.canExecute) return failure(state);
  if (evaluation.unassignedIndices.length === 0) return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        hovTargets: {
          ...state.hovTargets,
          assignments: [
            ...state.hovTargets.assignments,
            ...evaluation.unassignedIndices,
          ],
        },
      },
      mpCapacity,
    ),
  );
}

export type AssignAllHovGainUpgradesInput = {
  readonly state: MpmState;
  readonly mpCapacity: number;
};

export function resolveAssignAllMpmHovGainUpgrades(
  input: AssignAllHovGainUpgradesInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity } = input;
  if (!isMpmHovGainUpgradesActive(state)) return failure(state);
  const evaluation = resolveMpmHovGainUpgradesAssignAllState({
    mpCapacity,
    mpm: state,
  });
  if (!evaluation.canExecute) return failure(state);
  if (evaluation.unassignedTargets.length === 0) return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        hovGainUpgrades: {
          ...state.hovGainUpgrades,
          assignments: [
            ...state.hovGainUpgrades.assignments,
            ...evaluation.unassignedTargets,
          ],
        },
      },
      mpCapacity,
    ),
  );
}

export interface AssignAllAmplifierPanelsInput {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly hovAmplifierEntries: readonly MpmAssignAllAmplifierEntryInput[];
}

export function resolveAssignAllMpmAmplifierPanels(
  input: AssignAllAmplifierPanelsInput,
): MpmAssignmentCommandResult {
  const { state, mpCapacity, hovAmplifierEntries } = input;
  if (!isMpmAmplifierPanelsActive(state)) return failure(state);
  const evaluation = resolveMpmAmplifierPanelAssignAllState({
    mpCapacity,
    mpm: state,
    hovAmplifier: { entries: hovAmplifierEntries },
  });
  if (!evaluation.canExecute) return failure(state);
  if (evaluation.unassignedEntryIds.length === 0) return failure(state);
  return success(
    applyAssignment(
      {
        ...state,
        amplifierPanels: {
          ...state.amplifierPanels,
          assignments: [
            ...state.amplifierPanels.assignments,
            ...evaluation.unassignedEntryIds,
          ],
        },
      },
      mpCapacity,
    ),
  );
}

export function resolveClearMpmExplorationAssignments(
  state: MpmState,
): MpmAssignmentCommandResult {
  if (state.exploration.assignedSlotIds.length === 0) return failure(state);
  return success(
    applyRemoval({
      ...state,
      exploration: {
        ...state.exploration,
        assignedSlotIds: [],
      },
    }),
  );
}

export function resolveAssignAllMpmExploration(input: {
  readonly state: MpmState;
  readonly mpCapacity: number;
  readonly purchasedMpSlots: readonly ExplorationMpSlotDefinition[];
}): MpmAssignmentCommandResult {
  if (!input.state.exploration.enabled) return failure(input.state);
  const evaluation = resolveMpmExplorationAssignAllState({
    mpCapacity: input.mpCapacity,
    mpm: input.state,
    purchasedMpSlots: input.purchasedMpSlots,
  });
  if (!evaluation.canExecute) return failure(input.state);
  return success(
    applyAssignment(
      {
        ...input.state,
        exploration: {
          ...input.state.exploration,
          assignedSlotIds: [
            ...input.state.exploration.assignedSlotIds,
            ...evaluation.unassignedSlotIds,
          ],
        },
      },
      input.mpCapacity,
    ),
  );
}
